import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellColor, CellState, Puzzle, WordPlacement } from "../types";
import { GAME_STATE_KEY, PROGRESS_KEY, puzzleKey, getUtcToday } from "../lib/constants";

interface SavedGameState {
  grid: (string | null)[][];
  swapsUsed: number;
  maxSwaps?: number;
  startedAt?: number;   // timestamp of first visit
  solvedAt?: number;    // timestamp of solve (if completed)
  completedAtDate?: string; // UTC date of first completion (win or over-limit)
  elapsedMs?: number;   // legacy field (ignored, computed from timestamps)
  focusedMs?: number;   // accumulated ms while tab was visible
  winSeen?: boolean;
  loseSeen?: boolean;
}

function saveGameState(puzzleId: string, state: SavedGameState): void {
  try {
    const all = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}");
    all[puzzleId] = state;
    localStorage.setItem(GAME_STATE_KEY, JSON.stringify(all));
  } catch {
    // localStorage unavailable
  }
}

function loadGameState(puzzleId: string): SavedGameState | null {
  try {
    const all = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}");
    return all[puzzleId] ?? null;
  } catch {
    return null;
  }
}

export function clearGameState(puzzleId: string): void {
  try {
    const all = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}");
    delete all[puzzleId];
    localStorage.setItem(GAME_STATE_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

function getPuzzleId(puzzle: Puzzle): string {
  if (puzzle.date === "random") return `random:${puzzle.size}`;
  return puzzleKey(puzzle.size, puzzle.date);
}

function buildSolutionGrid(puzzle: Puzzle): (string | null)[][] {
  const rows = puzzle.grid.length;
  const cols = puzzle.grid[0].length;
  const solution: (string | null)[][] = Array.from({ length: rows }, () =>
    Array(cols).fill(null)
  );

  for (const wp of puzzle.words) {
    for (let i = 0; i < wp.word.length; i++) {
      const r = wp.direction === "down" ? wp.startRow + i : wp.startRow;
      const c = wp.direction === "across" ? wp.startCol + i : wp.startCol;
      solution[r][c] = wp.word[i];
    }
  }

  return solution;
}

function getWordsForCell(
  row: number,
  col: number,
  words: WordPlacement[]
): WordPlacement[] {
  return words.filter((wp) => {
    if (wp.direction === "across") {
      return (
        wp.startRow === row &&
        col >= wp.startCol &&
        col < wp.startCol + wp.word.length
      );
    } else {
      return (
        wp.startCol === col &&
        row >= wp.startRow &&
        row < wp.startRow + wp.word.length
      );
    }
  });
}

function computeCellColor(
  row: number,
  col: number,
  currentGrid: (string | null)[][],
  solutionGrid: (string | null)[][],
  words: WordPlacement[]
): CellColor {
  const letter = currentGrid[row][col];
  if (letter === null) return "empty";

  const correctLetter = solutionGrid[row][col];

  if (letter === correctLetter) return "green";

  const cellWords = getWordsForCell(row, col, words);
  if (cellWords.length === 0) return "grey";

  for (const wp of cellWords) {
    const wordLetters: string[] = [];

    for (let i = 0; i < wp.word.length; i++) {
      const r = wp.direction === "down" ? wp.startRow + i : wp.startRow;
      const c = wp.direction === "across" ? wp.startCol + i : wp.startCol;
      wordLetters.push(solutionGrid[r][c]!);
    }

    let solutionCount = wordLetters.filter((l) => l === letter).length;

    for (let i = 0; i < wp.word.length; i++) {
      const r = wp.direction === "down" ? wp.startRow + i : wp.startRow;
      const c = wp.direction === "across" ? wp.startCol + i : wp.startCol;
      if (currentGrid[r][c] === solutionGrid[r][c] && currentGrid[r][c] === letter) {
        solutionCount--;
      }
    }

    if (solutionCount > 0) {
      let priorYellowCount = 0;
      for (let i = 0; i < wp.word.length; i++) {
        const r = wp.direction === "down" ? wp.startRow + i : wp.startRow;
        const c = wp.direction === "across" ? wp.startCol + i : wp.startCol;
        if (r === row && c === col) break;
        if (
          currentGrid[r][c] === letter &&
          currentGrid[r][c] !== solutionGrid[r][c]
        ) {
          priorYellowCount++;
        }
      }

      if (priorYellowCount < solutionCount) {
        return "yellow";
      }
    }
  }

  return "grey";
}

export function useGame(puzzle: Puzzle) {
  const solutionGrid = useMemo(() => buildSolutionGrid(puzzle), [puzzle]);
  const puzzleId = useMemo(() => getPuzzleId(puzzle), [puzzle]);
  // Check if progress says this puzzle is already solved (e.g., synced from another device)
  // Exclude failed attempts (overLimit) — those are retries, not sync completions.
  const progressSolved = useMemo(() => {
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      if (!raw) return false;
      const progress = JSON.parse(raw);
      const entry = progress[puzzleId];
      return entry?.solved === true && !entry?.overLimit;
    } catch { return false; }
  }, [puzzleId]);

  const initialSaved = useMemo(() => {
    const saved = loadGameState(puzzleId);
    if (saved && saved.grid.length === puzzle.grid.length) {
      const savedLetters = saved.grid.flat().filter(Boolean).sort().join("");
      const puzzleLetters = puzzle.grid.flat().filter(Boolean).sort().join("");
      if (savedLetters === puzzleLetters) return saved;
      // Puzzle changed (word list update) — discard stale state
      clearGameState(puzzleId);
    }
    return null;
  }, [puzzleId, puzzle]);

  const [currentGrid, setCurrentGrid] = useState<(string | null)[][]>(() => {
    // If solved via sync (another device), snap to solution
    if (progressSolved && !initialSaved?.solvedAt) {
      return solutionGrid.map((r) => [...r]);
    }
    if (initialSaved) return initialSaved.grid;
    return puzzle.grid.map((row) => [...row]);
  });

  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const [swapsUsed, setSwapsUsed] = useState(() => {
    if (initialSaved) return initialSaved.swapsUsed;
    return 0;
  });

  const isWon = useMemo(() => {
    for (let r = 0; r < currentGrid.length; r++) {
      for (let c = 0; c < currentGrid[0].length; c++) {
        if (currentGrid[r][c] !== solutionGrid[r][c]) return false;
      }
    }
    return true;
  }, [currentGrid, solutionGrid]);

  // Timer: store startedAt timestamp on first visit. Elapsed is computed on demand.
  // On solve, store solvedAt so elapsed = solvedAt - startedAt (frozen).
  const [startedAt] = useState(() => initialSaved?.startedAt ?? Date.now());
  const [solvedAt, setSolvedAt] = useState<number | null>(() => initialSaved?.solvedAt ?? null);
  const [completedAtDate, setCompletedAtDate] = useState<string | null>(() => initialSaved?.completedAtDate ?? null);
  const isLost = !isWon && swapsUsed >= puzzle.maxSwaps && swapsUsed > 0;

  // Active time: accumulate ms only while the tab is visible.
  // focusedMsRef holds confirmed accumulated time (flushed on hide or solve).
  // lastVisibleAtRef marks when the current visible segment started.
  // Live active time = focusedMsRef.current + (now - lastVisibleAtRef.current).
  const focusedMsRef = useRef(initialSaved?.focusedMs ?? 0);
  const lastVisibleAtRef = useRef(Date.now());
  const [focusedMs, setFocusedMs] = useState(() => initialSaved?.focusedMs ?? 0);

  useEffect(() => {
    if (solvedAt) return;

    const flushActive = () => {
      const now = Date.now();
      focusedMsRef.current += now - lastVisibleAtRef.current;
      lastVisibleAtRef.current = now;
      setFocusedMs(focusedMsRef.current);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        flushActive();
      } else {
        lastVisibleAtRef.current = Date.now();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      // Flush on unmount (e.g. navigating to another puzzle or stats).
      // setState won't trigger a persist effect during unmount, so save directly.
      const now = Date.now();
      focusedMsRef.current += now - lastVisibleAtRef.current;
      lastVisibleAtRef.current = now;
      const saved = loadGameState(puzzleId);
      if (saved) {
        saved.focusedMs = focusedMsRef.current;
        saveGameState(puzzleId, saved);
      }
    };
  }, [solvedAt, puzzleId]);

  // Compute live focused time (includes current visible segment)
  const getLiveFocusedMs = useCallback(() => {
    if (solvedAt) return focusedMsRef.current;
    return focusedMsRef.current + (Date.now() - lastVisibleAtRef.current);
  }, [solvedAt]);

  // Freeze time and record completion date when puzzle is solved or lost
  useEffect(() => {
    if ((isWon || isLost) && !solvedAt) {
      const now = Date.now();
      setSolvedAt(now);
      focusedMsRef.current += now - lastVisibleAtRef.current;
      lastVisibleAtRef.current = now;
      setFocusedMs(focusedMsRef.current);
    }
    if ((isWon || isLost) && !completedAtDate) {
      setCompletedAtDate(getUtcToday());
    }
  }, [isWon, isLost, solvedAt, completedAtDate]);

  // Track whether win/lose modals have been shown (persisted)
  // If puzzle was solved via sync (another device), suppress the modal
  const [winSeen, setWinSeen] = useState(() => {
    if (progressSolved && !initialSaved?.solvedAt) return true;
    return initialSaved?.winSeen ?? false;
  });
  const [loseSeen, setLoseSeen] = useState(() => {
    return initialSaved?.loseSeen ?? false;
  });

  const markWinSeen = useCallback(() => setWinSeen(true), []);
  const markLoseSeen = useCallback(() => setLoseSeen(true), []);

  // Persist game state on meaningful changes (not every tick)
  useEffect(() => {
    saveGameState(puzzleId, { grid: currentGrid, swapsUsed, maxSwaps: puzzle.maxSwaps, startedAt, solvedAt: solvedAt ?? undefined, completedAtDate: completedAtDate ?? undefined, focusedMs, winSeen, loseSeen });
  }, [puzzleId, currentGrid, swapsUsed, puzzle.maxSwaps, startedAt, solvedAt, completedAtDate, focusedMs, winSeen, loseSeen]);

  const cellStates = useMemo((): CellState[][] => {
    const rows = currentGrid.length;
    const cols = currentGrid[0].length;
    const states: CellState[][] = [];

    for (let r = 0; r < rows; r++) {
      const row: CellState[] = [];
      for (let c = 0; c < cols; c++) {
        const letter = currentGrid[r][c];
        const color =
          letter === null
            ? ("empty" as CellColor)
            : computeCellColor(r, c, currentGrid, solutionGrid, puzzle.words);
        row.push({
          letter,
          color,
          isSelected:
            selectedCell !== null &&
            selectedCell.row === r &&
            selectedCell.col === c,
          isLocked: color === "green",
          row: r,
          col: c,
        });
      }
      states.push(row);
    }

    return states;
  }, [currentGrid, selectedCell, solutionGrid, puzzle.words]);

  const swapsRemaining = puzzle.maxSwaps - swapsUsed;

  const isCellSwappable = useCallback(
    (row: number, col: number): boolean => {
      const letter = currentGrid[row][col];
      if (letter === null) return false;
      const color = computeCellColor(row, col, currentGrid, solutionGrid, puzzle.words);
      return color !== "green";
    },
    [currentGrid, solutionGrid, puzzle.words]
  );

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (isWon) return;

      const letter = currentGrid[row][col];
      if (letter === null) return;

      const color = computeCellColor(
        row,
        col,
        currentGrid,
        solutionGrid,
        puzzle.words
      );
      if (color === "green") return;

      if (selectedCell === null) {
        setSelectedCell({ row, col });
      } else if (selectedCell.row === row && selectedCell.col === col) {
        setSelectedCell(null);
      } else {
        const selectedLetter = currentGrid[selectedCell.row][selectedCell.col];
        const targetLetter = currentGrid[row][col];
        if (selectedLetter === targetLetter) {
          // Don't waste a swap exchanging identical letters
          setSelectedCell(null);
          return;
        }
        setCurrentGrid((prev) => {
          const next = prev.map((r) => [...r]);
          const temp = next[selectedCell.row][selectedCell.col];
          next[selectedCell.row][selectedCell.col] = next[row][col];
          next[row][col] = temp;
          return next;
        });
        setSwapsUsed((prev) => prev + 1);
        setSelectedCell(null);
      }
    },
    [selectedCell, isWon, currentGrid, solutionGrid, puzzle.words]
  );

  const handleSwap = useCallback(
    (fromRow: number, fromCol: number, toRow: number, toCol: number) => {
      if (isWon) return;
      if (fromRow === toRow && fromCol === toCol) return;
      if (!isCellSwappable(fromRow, fromCol) || !isCellSwappable(toRow, toCol)) return;
      const fromLetter = currentGrid[fromRow][fromCol];
      const toLetter = currentGrid[toRow][toCol];
      if (fromLetter === toLetter) return;

      setCurrentGrid((prev) => {
        const next = prev.map((r) => [...r]);
        const temp = next[fromRow][fromCol];
        next[fromRow][fromCol] = next[toRow][toCol];
        next[toRow][toCol] = temp;
        return next;
      });
      setSwapsUsed((prev) => prev + 1);
      setSelectedCell(null);
    },
    [isWon, isCellSwappable, currentGrid]
  );

  return {
    cellStates,
    selectedCell,
    swapsUsed,
    swapsRemaining,
    isWon,
    isLost,
    startedAt,
    solvedAt,
    focusedMs,
    getLiveFocusedMs,
    winSeen,
    loseSeen,
    markWinSeen,
    markLoseSeen,
    handleCellClick,
    handleSwap,
    isCellSwappable,
  };
}
